// Membership Tier Types for Flash-Read

export type MembershipTier = 'guest' | 'starter' | 'reader' | 'ultra';

// Tier feature definitions — CANONICAL SOURCE OF TRUTH
export const TIER_FEATURES = {
  guest: {
    maxBooks: 0,
    xpEnabled: false,
    maxStreak: 0,
    canUploadTxt: false,
    canUploadEpub: false,
    uploadWordLimit: 0,
    hasSessionHistory: false,
    hasAnalytics: false,
    hasAchievements: false,
    hasDailyChallenges: false,
    hasProgressPersistence: false,
    hasCloudSync: false,
    hasHistoryExport: false,
    hasStreakFreezes: false,
    streakMultiplierBonus: 0,
    readerCanControlSpeed: true,
    readerCanNavigateSections: false,
    readerCanSkipTime: false,
    readerCanUseKeyboardShortcuts: true,
    readerForcedRamp: false,
    trialSessions: 3,
    freeStoriesLimit: 3,
  },
  starter: {
    maxBooks: -1,
    xpEnabled: false,
    maxStreak: 0,
    canUploadTxt: true,
    canUploadEpub: false,
    uploadWordLimit: -1,
    hasSessionHistory: true,
    hasAnalytics: false,
    hasAchievements: 'basic',
    hasDailyChallenges: false,
    hasProgressPersistence: true,
    hasCloudSync: false,
    hasHistoryExport: false,
    hasStreakFreezes: false,
    streakMultiplierBonus: 0,
    readerCanControlSpeed: true,
    readerCanNavigateSections: false,
    readerCanSkipTime: false,
    readerCanUseKeyboardShortcuts: true,
    readerForcedRamp: false,
    trialSessions: -1,
    freeStoriesLimit: -1,
  },
  reader: {
    maxBooks: 5,
    xpEnabled: true,
    maxStreak: 7,
    canUploadTxt: true,
    canUploadEpub: true,
    uploadWordLimit: -1,
    hasSessionHistory: true,
    hasAnalytics: true,
    hasAchievements: 'full',
    hasDailyChallenges: true,
    hasProgressPersistence: true,
    hasCloudSync: false,
    hasHistoryExport: false,
    hasStreakFreezes: false,
    streakMultiplierBonus: 0,
    readerCanControlSpeed: true,
    readerCanNavigateSections: true,
    readerCanSkipTime: true,
    readerCanUseKeyboardShortcuts: true,
    readerForcedRamp: false,
    trialSessions: -1,
    freeStoriesLimit: -1,
  },
  ultra: {
    maxBooks: -1,
    xpEnabled: true,
    maxStreak: -1,
    canUploadTxt: true,
    canUploadEpub: true,
    uploadWordLimit: -1,
    hasSessionHistory: true,
    hasAnalytics: true,
    hasAchievements: 'full',
    hasDailyChallenges: true,
    hasProgressPersistence: true,
    hasCloudSync: true,
    hasHistoryExport: true,
    hasStreakFreezes: true,
    streakMultiplierBonus: 0.25,
    readerCanControlSpeed: true,
    readerCanNavigateSections: true,
    readerCanSkipTime: true,
    readerCanUseKeyboardShortcuts: true,
    readerForcedRamp: false,
    trialSessions: -1,
    freeStoriesLimit: -1,
  },
} as const;

// Tier pricing (monthly only, in USD)
export const TIER_PRICING = {
  starter: 4.49,
  reader: 6.99,
  ultra: 9.99,
} as const;

// Tier display information
export const TIER_INFO: Record<MembershipTier, {
  name: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  guest: {
    name: 'Guest',
    description: '3 free stories',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
  },
  starter: {
    name: 'Starter',
    description: 'Everything you need to start',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
  },
  reader: {
    name: 'Reader',
    description: 'The complete experience',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
  },
  ultra: {
    name: 'Ultra',
    description: 'For serious readers',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-300',
  },
};

// Marketing-friendly benefit strings per tier (derived from TIER_FEATURES)
export const TIER_BENEFITS: Record<MembershipTier, string[]> = {
  guest: [
    '3 free stories',
    'Basic speed tracking',
    'No account required',
  ],
  starter: [
    'Unlimited stories',
    'Unlimited .txt uploads',
    'Session history log',
    'Persistent progress',
    'Basic achievements',
    'Speed control',
  ],
  reader: [
    'Everything in Starter',
    '5 active books',
    '.epub uploads with chapters',
    'XP & Levels',
    'Full achievements (100+)',
    'Daily challenges',
    'Streak tracking + multiplier',
    'Basic analytics & trends',
    'Full reader controls',
  ],
  ultra: [
    'Everything in Reader',
    'Unlimited books',
    'Cloud Sync across devices',
    'Advanced analytics + export',
    'History export (CSV)',
    'Streak freezes (1/month)',
    '+0.25x streak bonus',
  ],
};

// Type for feature keys
export type TierFeatureKey = keyof typeof TIER_FEATURES.reader;

// User profile from database
export interface UserProfileData {
  id: string;
  email: string;
  membership_tier: MembershipTier;
  tier_expires_at: string | null;
  total_xp: number;
  current_level: number;
  lifetime_xp: number;
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
  freezes_available: number;
  freezes_used: number;
  created_at: string;
  updated_at: string;
  // NEW fields for achievements:
  books_completed: number;       // Total books fully read
  streak_multiplier: number;     // Current streak multiplier (default 1)
  challenges_completed: number;  // Total daily challenges completed
  daily_challenge_streak: number; // Consecutive days with all challenges done
}

// Unlocked achievement
export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  unlocked_at: string;
  xp_earned: number;
}

// Daily challenge progress
export interface UserDailyChallenge {
  id: string;
  user_id: string;
  challenge_date: string;
  challenge_id: string;
  completed: boolean;
  completed_at: string | null;
  xp_earned: number;
}

// Ultra sync table types for book progress and reading sessions
export interface UserBookProgress {
  id?: string;
  user_id: string;
  book_id: string;
  book_title: string;
  current_chapter: number;
  current_position: number;
  progress_percentage: number;
  last_read_at: string;
  created_at?: string;
  updated_at?: string;
}

export interface UserChapterProgress {
  id?: string;
  user_book_progress_id: string;
  chapter_index: number;
  position: number;
  completed: boolean;
  updated_at?: string;
}

export interface UserReadingSession {
  id?: string;
  user_id: string;
  words_read: number;
  duration_seconds: number;
  avg_wpm: number;
  max_wpm: number;
  session_date: string;
  created_at?: string;
}

// Local storage record types for sync
export interface ReadingSessionLocal {
  id?: string;
  userId: string;
  wordsRead: number;
  durationSeconds: number;
  avgWpm: number;
  maxWpm: number;
  sessionDate: string; // YYYY-MM-DD
  createdAt: string;
  synced: boolean;
  remoteId?: string;
}

export interface DailyChallengeLocal {
  id?: string;
  userId: string;
  challengeDate: string;
  challengeId: string;
  completed: boolean;
  completedAt: string | null;
  xpEarned: number;
  synced: boolean;
  remoteId?: string;
}
